import React, { useEffect, useState } from 'react';
import { Trophy, Package } from 'lucide-react';
import api from '../../services/api';

// Top produits — affichage en barres horizontales custom (pas recharts).
// On garde recharts pour les graphiques temporels et on dessine ici une simple
// liste classée façon « podium » qui contraste visuellement avec l'AreaChart.

// Accepte soit `period={key,label,from,to}` (nouveau), soit `days={number}` (rétro-compat).
export default function TopProduitsChart({ period, days = 7, limit = 5 }) {
  const [data, setData]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(false);

  const paramsKey = period
    ? `from=${period.from}&to=${period.to}&limit=${limit}`
    : `days=${days}&limit=${limit}`;
  const labelPeriode = period?.label || `${days} derniers jours`;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    api.get(`/dashboard/top-produits?${paramsKey}`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [paramsKey]);

  const max = data.length > 0 ? Math.max(...data.map(p => p.quantite)) : 0;

  // Dégradé de couleurs : or pour le 1er, dégradé jaune → orange pour le reste
  const podiumColor = (rank) => {
    if (rank === 0) return 'from-yellow-400 to-yellow-600';
    if (rank === 1) return 'from-yellow-500/80 to-yellow-600/80';
    if (rank === 2) return 'from-yellow-600/70 to-orange-600/70';
    return 'from-yellow-700/50 to-orange-700/50';
  };

  const medal = (rank) => {
    if (rank === 0) return '🥇';
    if (rank === 1) return '🥈';
    if (rank === 2) return '🥉';
    return `${rank + 1}`;
  };

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Trophy size={16} className="text-yellow-400" />
            Meilleures ventes
          </h3>
          <p className="text-gray-500 text-xs mt-0.5">
            Top {limit} produits — {labelPeriode.toLowerCase()}
          </p>
        </div>
      </div>

      {loading ? (
        <div className="h-48 flex items-center justify-center">
          <div className="animate-spin w-6 h-6 border-2 border-yellow-500 border-t-transparent rounded-full" />
        </div>
      ) : error ? (
        <p className="text-gray-600 text-sm text-center py-10">
          Impossible de charger les données
        </p>
      ) : data.length === 0 ? (
        <div className="text-center py-10">
          <Package size={32} className="text-gray-700 mx-auto mb-2" />
          <p className="text-gray-600 text-sm">Aucune vente sur la période</p>
        </div>
      ) : (
        <div className="space-y-3">
          {data.map((p, i) => {
            const pct = max > 0 ? (p.quantite / max) * 100 : 0;
            return (
              <div key={p.id} className="group">
                <div className="flex items-center justify-between text-sm mb-1.5">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <span
                      className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        i < 3 ? 'text-base' : 'bg-gray-800 text-gray-400'
                      }`}
                      title={`Rang ${i + 1}`}
                    >
                      {medal(i)}
                    </span>
                    <span className="text-gray-200 truncate font-medium">{p.nom}</span>
                  </div>
                  <span className="text-yellow-400 font-bold text-sm whitespace-nowrap ml-3">
                    {p.quantite} <span className="text-gray-500 font-normal text-xs">vendu{p.quantite > 1 ? 's' : ''}</span>
                  </span>
                </div>
                <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                  <div
                    className={`h-full bg-gradient-to-r ${podiumColor(i)} rounded-full transition-all duration-700 ease-out`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
